if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.hasVariants = this.dataset.hasVariants === 'true';

      this.productInfo =
        document.getElementById(`ProductInfo-${this.sectionId}`) ||
        this.closest('product-info');

      if (!this.productInfo) return;

      this.refreshDawnElements();

      this.openButton = this.querySelector('[data-sticky-open]');
      this.sheet = this.querySelector('[data-sticky-sheet]');

      this.backdrop = this.querySelector(
        '[data-sticky-close].sticky-atc__backdrop'
      );

      this.closeButtons = this.querySelectorAll(
        '[data-sticky-close]'
      );

      this.drawerOptions = Array.from(
        this.querySelectorAll('[data-sticky-option]')
      );

      this.addButton = this.querySelector('[data-sticky-add]');

      this.addButtonText = this.querySelector(
        '[data-sticky-add-text]'
      );

      this.message = this.querySelector(
        '[data-sticky-message]'
      );

      this.price = this.querySelector(
        '[data-sticky-price]'
      );

      if (!this.mainButton || !this.openButton) return;

      this.hidden = false;
      document.body.classList.add('sticky-atc-enabled');

      this.handleOpen = this.open.bind(this);
      this.handleAdd = this.addToCart.bind(this);

      this.handleOptionChange =
        this.changeOriginalOption.bind(this);

      this.handleOriginalChange =
        this.originalSelectionChanged.bind(this);

      this.handleKeydown = this.keydown.bind(this);
      this.handleClose = this.close.bind(this);

      this.openButton.addEventListener(
        'click',
        this.handleOpen
      );

      this.addButton.addEventListener(
        'click',
        this.handleAdd
      );

      this.closeButtons.forEach((button) => {
        button.addEventListener(
          'click',
          this.handleClose
        );
      });

      this.drawerOptions.forEach((select) => {
        select.addEventListener(
          'change',
          this.handleOptionChange
        );
      });

      this.productInfo.addEventListener(
        'change',
        this.handleOriginalChange
      );

      document.addEventListener(
        'keydown',
        this.handleKeydown
      );

      this.productObserver = new MutationObserver(
        (mutations) => {
          const relevantMutation = mutations.some(
            (mutation) => {
              const target = mutation.target;

              return !target.closest?.(
                'sticky-add-to-cart'
              );
            }
          );

          if (relevantMutation) {
            this.queueSync();
          }
        }
      );

      this.productObserver.observe(this.productInfo, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: [
          'disabled',
          'aria-disabled',
          'selected',
          'checked'
        ]
      });

      this.syncFromOriginal();
      this.updateState();
    }

    disconnectedCallback() {
      document.body.classList.remove(
        'sticky-atc-enabled',
        'sticky-atc-open'
      );

      window.clearTimeout(this.syncTimer);

      if (this.openButton) {
        this.openButton.removeEventListener(
          'click',
          this.handleOpen
        );
      }

      if (this.addButton) {
        this.addButton.removeEventListener(
          'click',
          this.handleAdd
        );
      }

      if (this.closeButtons) {
        this.closeButtons.forEach((button) => {
          button.removeEventListener(
            'click',
            this.handleClose
          );
        });
      }

      if (this.drawerOptions) {
        this.drawerOptions.forEach((select) => {
          select.removeEventListener(
            'change',
            this.handleOptionChange
          );
        });
      }

      if (this.productInfo) {
        this.productInfo.removeEventListener(
          'change',
          this.handleOriginalChange
        );
      }

      document.removeEventListener(
        'keydown',
        this.handleKeydown
      );

      if (this.productObserver) {
        this.productObserver.disconnect();
      }
    }

    refreshDawnElements() {
      this.variantPicker =
        this.productInfo.querySelector('variant-selects');

      this.mainButton =
        this.productInfo.querySelector(
          `#ProductSubmitButton-${this.sectionId}`
        ) ||
        this.productInfo.querySelector(
          'product-form button[type="submit"][name="add"]'
        );
    }

    queueSync() {
      window.clearTimeout(this.syncTimer);

      this.syncTimer = window.setTimeout(() => {
        this.refreshDawnElements();
        this.syncFromOriginal();
        this.updateState();
      }, 75);
    }

    resyncAfterVariantChange() {
      [0, 200, 600, 1200].forEach((delay) => {
        window.setTimeout(() => {
          this.refreshDawnElements();
          this.syncFromOriginal();
          this.updateState();
        }, delay);
      });
    }

    open() {
      this.refreshDawnElements();

      if (!this.hasVariants) {
        const buttonDisabled =
          this.mainButton.disabled ||
          this.mainButton.getAttribute(
            'aria-disabled'
          ) === 'true';

        if (!buttonDisabled) {
          this.mainButton.click();
        }

        return;
      }

      this.syncFromOriginal();
      this.updateState();

      this.sheet.hidden = false;
      this.backdrop.hidden = false;

      document.body.classList.add(
        'sticky-atc-open'
      );

      const firstEmptyOption =
        this.drawerOptions.find(
          (select) => !select.value
        );

      window.setTimeout(() => {
        (
          firstEmptyOption ||
          this.drawerOptions[0] ||
          this.addButton
        )?.focus();
      }, 50);
    }

    close() {
      if (!this.sheet || !this.backdrop) return;

      this.sheet.hidden = true;
      this.backdrop.hidden = true;

      document.body.classList.remove(
        'sticky-atc-open'
      );

      this.openButton?.focus();
    }

    keydown(event) {
      if (
        event.key === 'Escape' &&
        this.sheet &&
        !this.sheet.hidden
      ) {
        this.close();
      }
    }

    getOriginalGroup(position) {
      this.refreshDawnElements();

      if (!this.variantPicker) return null;

      const index = Number(position) - 1;

      const selects = Array.from(
        this.variantPicker.querySelectorAll('select')
      );

      if (selects.length) {
        return {
          type: 'select',
          element: selects[index]
        };
      }

      const fieldsets = Array.from(
        this.variantPicker.querySelectorAll('fieldset')
      );

      return {
        type: 'radios',
        element: fieldsets[index]
      };
    }

    changeOriginalOption(event) {
      const drawerSelect = event.currentTarget;

      const position =
        drawerSelect.dataset.optionPosition;

      const selectedDrawerOption =
        drawerSelect.selectedOptions[0];

      const valueId =
        selectedDrawerOption.value;

      const valueName =
        selectedDrawerOption.dataset.valueName || '';

      if (!valueId) {
        this.updateState();
        return;
      }

      const originalGroup =
        this.getOriginalGroup(position);

      if (
        !originalGroup ||
        !originalGroup.element
      ) {
        return;
      }

      if (originalGroup.type === 'select') {
        const originalOptions = Array.from(
          originalGroup.element.options
        );

        const matchingOption =
          originalOptions.find((option) => {
            const originalId =
              option.dataset.optionValueId ||
              option.value;

            return (
              String(originalId) ===
                String(valueId) ||
              option.value === valueName ||
              option.textContent.trim() ===
                valueName
            );
          });

        if (matchingOption) {
          originalGroup.element.value =
            matchingOption.value;

          originalGroup.element.dispatchEvent(
            new Event('change', {
              bubbles: true
            })
          );
        }
      }

      if (originalGroup.type === 'radios') {
        const originalInputs = Array.from(
          originalGroup.element.querySelectorAll(
            'input[type="radio"]'
          )
        );

        const matchingInput =
          originalInputs.find((input) => {
            const originalId =
              input.dataset.optionValueId ||
              input.value;

            return (
              String(originalId) ===
                String(valueId) ||
              input.value === valueName
            );
          });

        if (
          matchingInput &&
          !matchingInput.checked
        ) {
          matchingInput.click();
        }
      }

      this.resyncAfterVariantChange();
    }

    originalSelectionChanged(event) {
      if (
        event.target.closest?.(
          'sticky-add-to-cart'
        )
      ) {
        return;
      }

      this.resyncAfterVariantChange();
    }

    syncFromOriginal() {
      this.refreshDawnElements();

      if (!this.variantPicker) return;

      this.drawerOptions.forEach(
        (drawerSelect) => {
          const position =
            drawerSelect.dataset.optionPosition;

          const originalGroup =
            this.getOriginalGroup(position);

          if (
            !originalGroup ||
            !originalGroup.element
          ) {
            return;
          }

          let originalId = '';
          let originalName = '';

          if (
            originalGroup.type === 'select'
          ) {
            const selected =
              originalGroup.element
                .selectedOptions[0];

            if (
              selected &&
              originalGroup.element.value
            ) {
              originalId =
                selected.dataset.optionValueId ||
                selected.value;

              originalName =
                selected.textContent.trim();
            }
          }

          if (
            originalGroup.type === 'radios'
          ) {
            const checked =
              originalGroup.element.querySelector(
                'input[type="radio"]:checked'
              );

            if (checked) {
              originalId =
                checked.dataset.optionValueId ||
                checked.value;

              originalName = checked.value;
            }
          }

          const drawerOption = Array.from(
            drawerSelect.options
          ).find((option) => {
            return (
              String(option.value) ===
                String(originalId) ||
              option.dataset.valueName ===
                originalName
            );
          });

          drawerSelect.value = drawerOption
            ? drawerOption.value
            : '';
        }
      );
    }

    selectionIsComplete() {
      if (!this.hasVariants) return true;

      return this.drawerOptions.every(
        (select) => Boolean(select.value)
      );
    }

    getMainButtonText() {
      if (!this.mainButton) {
        return 'In winkelwagen';
      }

      const textElement =
        this.mainButton.querySelector('span');

      return textElement
        ? textElement.textContent.trim()
        : this.mainButton.textContent.trim();
    }

    updateState() {
      this.refreshDawnElements();

      if (!this.mainButton) return;

      const complete =
        this.selectionIsComplete();

      const mainButtonDisabled =
        this.mainButton.disabled ||
        this.mainButton.getAttribute(
          'aria-disabled'
        ) === 'true';

      if (!this.hasVariants) {
        this.openButton.disabled =
          mainButtonDisabled;

        this.openButton.textContent =
          this.getMainButtonText() ||
          'In winkelwagen';

        return;
      }

      this.openButton.disabled = false;
      this.openButton.textContent =
        'In winkelwagen';

      if (!complete) {
        this.addButton.disabled = true;

        this.addButtonText.textContent =
          'Kies je opties';

        this.message.textContent =
          'Maak eerst alle keuzes hierboven.';

        return;
      }

      const originalButtonText =
        this.getMainButtonText() ||
        'In winkelwagen';

      this.addButton.disabled =
        mainButtonDisabled;

      this.addButtonText.textContent =
        originalButtonText;

      this.message.textContent =
        mainButtonDisabled
          ? originalButtonText
          : '';
    }

    addToCart() {
      this.refreshDawnElements();

      if (
        !this.selectionIsComplete() ||
        !this.mainButton
      ) {
        this.updateState();
        return;
      }

      const mainButtonDisabled =
        this.mainButton.disabled ||
        this.mainButton.getAttribute(
          'aria-disabled'
        ) === 'true';

      if (mainButtonDisabled) {
        this.updateState();
        return;
      }

      this.close();
      this.mainButton.click();
    }
  }

  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCart
  );
}